import React, {useEffect, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Container,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    Typography
} from '@mui/material';
import {Link, useNavigate} from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import EmailIcon from '@mui/icons-material/Email';
import {getUser, saveUser} from '../helpers/crypto';
import {Constants} from "../components/Constants";

const API_URL = Constants.SERVER_URL;

const ProfilePage: React.FC = () => {
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: '', last_name: '', email: '', phone: '', post_address: '' });
    const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm_password: '' });
    const [showPasswords, setShowPasswords] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [userId, setUserId] = useState<number | null>(null);

    useEffect(() => {
        const user = getUser();
        if (!user) {
            navigate('/login');
            return;
        }
        setUserId(user.id);
        setForm({
            name: user.name || '',
            last_name: user.last_name || '',
            email: user.email || '',
            phone: user.phone || '',
            post_address: user.post_address || '',
        });
    }, [navigate]);

    const updateField = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm({ ...form, [field]: e.target.value });
    };

    const updatePassword = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setPasswords({ ...passwords, [field]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!form.name || !form.last_name) {
            setError("Ім'я та прізвище обов'язкові");
            return;
        }

        if (passwords.new_password && passwords.new_password !== passwords.confirm_password) {
            setError('Нові паролі не співпадають');
            return;
        }

        if (passwords.new_password && !passwords.current_password) {
            setError('Введіть поточний пароль');
            return;
        }

        setLoading(true);
        try {
            const body: any = {
                id: userId,
                name: form.name,
                last_name: form.last_name,
                email: form.email,
                phone: form.phone,
                post_address: form.post_address,
            };

            if (passwords.new_password) {
                body.current_password = passwords.current_password;
                body.new_password = passwords.new_password;
            }

            const res = await fetch(`${API_URL}${Constants.UPDATE_PROFILE_PREFIX}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const data = await res.json();
            if (data.success) {
                saveUser(data.user);
                setPasswords({ current_password: '', new_password: '', confirm_password: '' });
                setSuccess('Профіль успішно оновлено');
            } else {
                setError(data.error || 'Помилка оновлення');
            }
        } catch {
            setError("Помилка з'єднання з сервером");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f7f9fc', py: 4 }}>
            <Container maxWidth="sm">
                <Paper elevation={0} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3, border: '1px solid #e0e6ed' }}>
                    {/* Logo */}
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Box component={Link} to="/" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, textDecoration: 'none' }}>
                            <LocalHospitalIcon sx={{ color: '#0077CC', fontSize: 32 }} />
                            <Typography sx={{ fontWeight: 700, fontSize: 22, color: '#1a1a2e' }}>{Constants.APP_LABEL}</Typography>
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, mt: 3, mb: 0.5 }}>Мій профіль</Typography>
                        <Typography sx={{ color: '#555' }}>Редагуйте ваші персональні дані</Typography>
                    </Box>

                    {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

                    <Box component="form" onSubmit={handleSubmit}>
                        {/* Email (read-only) */}
                        <TextField
                            fullWidth label="Email" value={form.email} disabled
                            sx={{ mb: 2.5 }} size="small"
                            slotProps={{ input: { startAdornment: <InputAdornment position="start">
                                        <EmailIcon sx={{ color: '#999' }} /></InputAdornment> } }}
                        />

                        {/* Name fields */}
                        <Grid container spacing={2} sx={{ mb: 0.5 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField size="small"
                                    fullWidth label="Ім'я *" value={form.name} onChange={updateField('name')}
                                    slotProps={{ input: { startAdornment: <InputAdornment position="start">
                                                <PersonIcon sx={{ color: '#999' }} /></InputAdornment> } }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField size="small"
                                    fullWidth label="Прізвище *" value={form.last_name} onChange={updateField('last_name')}
                                    slotProps={{ input: { startAdornment: <InputAdornment position="start">
                                                <PersonIcon sx={{ color: '#999' }} /></InputAdornment> } }}
                                />
                            </Grid>
                        </Grid>

                        <TextField
                            fullWidth label="Телефон" value={form.phone} onChange={updateField('phone')}
                            sx={{ mb: 2.5, mt: 2 }} size="small"
                            slotProps={{ input: { startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: '#999' }} />
                            </InputAdornment> } }}
                        />

                        <TextField size="small"
                            fullWidth label="Адреса доставки" value={form.post_address} onChange={updateField('post_address')}
                            multiline rows={2} sx={{ mb: 3 }}
                            slotProps={{ input: { startAdornment: <InputAdornment position="start"><HomeIcon sx={{ color: '#999' }} />
                            </InputAdornment> } }}
                        />

                        {/* Change password section */}
                        <Divider sx={{ mb: 3 }}>
                            <Typography sx={{ color: '#999', fontSize: 13 }}>Зміна пароля (необов'язково)</Typography>
                        </Divider>

                        <TextField size="small"
                            fullWidth label="Поточний пароль" type={showPasswords ? 'text' : 'password'}
                            value={passwords.current_password} onChange={updatePassword('current_password')}
                            sx={{ mb: 2.5 }}
                            slotProps={{
                                input: {
                                    startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: '#999' }} /></InputAdornment>,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPasswords(!showPasswords)} edge="end">
                                                {showPasswords ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }
                            }}
                        />

                        <Grid container spacing={2} sx={{ mb: 0.5 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField size="small"
                                    fullWidth label="Новий пароль" type={showPasswords ? 'text' : 'password'}
                                    value={passwords.new_password} onChange={updatePassword('new_password')}
                                    slotProps={{ input: { startAdornment: <InputAdornment position="start">
                                                <LockIcon sx={{ color: '#999' }} /></InputAdornment> } }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField size="small"
                                    fullWidth label="Підтвердіть пароль" type={showPasswords ? 'text' : 'password'}
                                    value={passwords.confirm_password} onChange={updatePassword('confirm_password')}
                                    slotProps={{ input: { startAdornment: <InputAdornment position="start">
                                                <LockIcon sx={{ color: '#999' }} /></InputAdornment> } }}
                                />
                            </Grid>
                        </Grid>

                        <Button
                            type="submit" variant="contained" fullWidth size="large" disabled={loading}
                            sx={{ textTransform: 'none', borderRadius: 2, py: 1.5, mt: 3, mb: 2 }}
                        >
                            {loading ? 'Збереження...' : 'Зберегти зміни'}
                        </Button>

                        <Button
                            variant="text" fullWidth onClick={() => navigate('/')}
                            sx={{ textTransform: 'none', color: '#555' }}
                        >
                            ← Повернутися на головну
                        </Button>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default ProfilePage;
